import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building, FileText, CheckCircle, Waves, Droplets, Shield, ArrowRight, Clock, DollarSign, Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { businessRef, POSTAL_ADDRESS, BUSINESS_PHONE } from "@/lib/business";

export default function Services() {
  const { serviceId } = useParams();

  const servicesData = {
    "pre-sale": {
      seoTitle: "Pre-Sale Building Inspection Darwin",
      icon: Building,
      title: "Pre-Sale Property Inspections",
      subtitle: "Get Your Home Ready for Market with Confidence",
      description: "Our pre-sale inspections help sellers identify and address potential issues before listing. Visual condition reports (AS4349.1-aligned) provide clarity for both sellers and buyers, reducing renegotiations and protecting deal flow.",
      image: "/assets/img/house-for-sale.jpg",
      includes: [
        "Visual condition assessment",
        "AS4349.1-aligned reporting",
        "Digital report with photos",
        "Clear, simple format",
        "72-hour delivery from inspection",
        "Visible issues only — no structural opinions",
        "Identify issues before listing",
        "Reduce buyer renegotiations"
      ],
      pricing: "From $325 incl. GST",
      note: "Flat-rate pricing with no surprises",
      faqs: [
        {
          question: "Why get a pre-sale inspection?",
          answer: "A pre-sale inspection helps you identify visible issues before buyers discover them, reducing last-minute negotiations and protecting your sale timeline. Our clear reports give buyers confidence."
        },
        {
          question: "What's included in the report?",
          answer: "You'll receive a digital report with photos showing visible condition issues. We focus on what can be seen \u2014 no structural opinions or grey areas. Reports are AS4349.1-aligned."
        },
        {
          question: "How long until I receive the report?",
          answer: "Reports are delivered within 72 hours of inspection, keeping your property timeline moving forward."
        }
      ]
    },
    "strata": {
      seoTitle: "Strata Report Darwin NT",
      icon: Building,
      title: "Strata Reports",
      subtitle: "Independent Reports for Strata and Unit Properties",
      description: "Independent strata reports for unit and townhouse buyers, owners and sellers in Darwin and the Top End. Every strata property is different, so each report is scoped and priced to the property and what you need to know. Contact us and we will confirm the scope and price before we start.",
      image: "/assets/img/inspection-checklist.jpg",
      includes: [
        "Scoped to your property and your questions",
        "Independent, impartial reporting",
        "Digital report with photos",
        "Clear, plain-English findings",
        "Fully insured, qualified inspectors",
        "Price confirmed up front, before we start"
      ],
      pricing: "Contact us for pricing",
      priceOnRequest: true,
      note: "Every strata property is different, so each report is quoted to suit",
      faqs: [
        {
          question: "How much does a strata report cost?",
          answer: "Strata reports are priced individually because the scope depends on the property and what you need covered. Contact us with the address and what you are trying to find out, and we will confirm the price before we start."
        },
        {
          question: "Do you offer strata reports in Darwin?",
          answer: "Yes. Aussie Inspections provides independent strata reports for unit and townhouse properties in Darwin, Palmerston and the surrounding Top End. Our inspectors are fully insured and hold relevant qualifications."
        },
        {
          question: "How do I arrange a strata report?",
          answer: "Call 0413 24 55 19 or send an enquiry through our contact page with the property address and your timeframe. We will come back to you with the scope and the price."
        }
      ]
    },
    "building-status": {
      seoTitle: "Building Status Report Darwin NT",
      icon: FileText,
      title: "Building Status Reports",
      subtitle: "Visual and Digital Check for All Things Approval Related",
      description: "Our building status reports provide a comprehensive visual inspection focused on approval-related matters. Perfect for property transactions requiring verification of building approvals and compliance status.",
      image: "/assets/img/building-status-report.jpg",
      includes: [
        "Visual inspection of property",
        "Digital documentation with photos",
        "Approval status assessment",
        "Compliance verification",
        "Clear, easy-to-read format",
        "72-hour report delivery",
        "Professional photography",
        "Email delivery"
      ],
      pricing: "From $325 incl. GST",
      note: "Standard flat-rate pricing",
      faqs: [
        {
          question: "What is a building status report?",
          answer: "A building status report is a visual inspection documenting approval-related aspects of the property. It helps verify compliance and approval status for property transactions."
        },
        {
          question: "Is this different from a building condition report?",
          answer: "Yes. Building status focuses on approvals and compliance, while building condition reports assess the physical condition and aesthetics of the building."
        }
      ]
    },
    "building-condition": {
      seoTitle: "Pre-Purchase Building Inspection Darwin",
      icon: CheckCircle,
      title: "Building Inspection Reports",
      subtitle: "Complete Inspection with Condition & Remediation Reports",
      description: "Comprehensive visual inspection of building condition and aesthetics. All building inspection reports include both our condition report and remediation report, providing cost estimates to address identified issues.",
      image: "/assets/img/building-condition-report.jpg",
      includes: [
        "Visual condition inspection",
        "Condition Report included",
        "Remediation Report included",
        "Digital report with photos",
        "Cost estimates for repairs",
        "72-hour delivery",
        "Clear, simple format",
        "No structural opinions"
      ],
      pricing: "From $455 incl. GST",
      note: "Includes Condition & Remediation Reports",
      faqs: [
        {
          question: "What's included in the remediation report?",
          answer: "Every building condition report includes our remediation report at no extra cost. This provides estimated costs to address the issues identified during the inspection."
        },
        {
          question: "Do you provide structural opinions?",
          answer: "No, we keep it simple. We focus on visible issues only \u2014 no structural opinions or grey areas. This keeps reports clear and straightforward."
        },
        {
          question: "What if major issues are found?",
          answer: "Our remediation report will provide cost estimates for addressing issues. This helps you make informed decisions about the property."
        }
      ]
    },
    "pool": {
      seoTitle: "Pool Inspection & Remediation Report Darwin",
      icon: Waves,
      title: "Pool Remediation Reports",
      subtitle: "Ensure Your Pool Meets Government Requirements",
      description: "Our pool inspections help you understand if your pool will meet Government requirements. Get it right the first time and ensure compliance before official inspections.",
      image: "/assets/img/pool-report.jpg",
      includes: [
        "Visual pool barrier inspection",
        "Gate and fence assessment",
        "Compliance guidance",
        "Digital report with photos",
        "Government requirements check",
        "72-hour delivery",
        "Advice for compliance",
        "Pre-inspection preparation"
      ],
      pricing: "From $260 incl. GST",
      note: "Pass your official inspection first time",
      faqs: [
        {
          question: "Will this report guarantee I pass the official inspection?",
          answer: "Our report provides guidance on meeting Government requirements. While we can't guarantee official outcomes, our inspections help you identify and address issues before the official inspection."
        },
        {
          question: "What if my pool doesn't meet requirements?",
          answer: "Our report will identify areas that may not comply and provide guidance on what needs to be addressed. Most issues are straightforward to fix."
        },
        {
          question: "How long until I receive the report?",
          answer: "All reports are delivered within 72 hours of inspection."
        }
      ]
    },
    "pest": {
      seoTitle: "Pest & Termite Inspection Darwin",
      icon: Bug,
      title: "Pest Reports",
      subtitle: "Comprehensive Pest and Termite Inspections",
      description: "Thorough pest and termite inspection to identify current activity, damage, and potential risks. Essential for property purchases in Darwin's tropical climate where termites are extremely common.",
      image: "/assets/img/pest-inspection.jpg",
      includes: [
        "Comprehensive pest inspection",
        "Termite activity assessment",
        "Evidence of past damage",
        "Risk area identification",
        "Digital report with photos",
        "72-hour delivery",
        "Treatment recommendations",
        "Prevention advice"
      ],
      pricing: "From $325 incl. GST",
      note: "Essential for Darwin properties",
      faqs: [
        {
          question: "Why is a pest inspection important in Darwin?",
          answer: "Darwin's tropical climate creates ideal conditions for termites and other pests. Termite damage can be extensive and costly to repair. A pest inspection identifies current activity and potential risks before you buy."
        },
        {
          question: "What types of pests do you check for?",
          answer: "Our inspection covers termites (the main concern in Darwin), borers, and other timber-destroying pests. We also note evidence of rodents and other pest activity."
        },
        {
          question: "Can I combine this with a building inspection?",
          answer: "Yes, many clients combine pest and building inspections for a comprehensive property assessment. Contact us for package pricing."
        }
      ]
    },
    "plumbing": {
      seoTitle: "Plumbing Inspection Report Darwin",
      icon: Droplets,
      title: "Plumbing Reports",
      subtitle: "Comprehensive Assessment of Plumbing Systems",
      description: "Complete visual inspection of all plumbing systems and fixtures. Our detailed reports identify visible issues with water supply, drainage, fixtures, and potential problem areas.",
      image: "/assets/img/plumbing.jpg",
      includes: [
        "Visual inspection of all plumbing",
        "Water supply assessment",
        "Drainage system check",
        "Fixture condition review",
        "Digital report with photos",
        "72-hour delivery",
        "Clear documentation",
        "Visible issues only"
      ],
      pricing: "From $520 incl. GST",
      note: "Full scope visual inspection",
      faqs: [
        {
          question: "What's included in a plumbing report?",
          answer: "We conduct a full visual inspection of all accessible plumbing systems, including water supply, drainage, and fixtures. The report documents visible issues with photos."
        },
        {
          question: "Do you test water pressure or flow rates?",
          answer: "We focus on visual inspection. Our reports document what can be seen \u2014 we don't perform invasive testing or provide technical measurements."
        }
      ]
    },
    "electrical": {
      seoTitle: "Electrical Inspection Report Darwin",
      icon: Shield,
      title: "Electrical Reports",
      subtitle: "Full Scope Electrical Assessment",
      description: "Comprehensive electrical inspections covering all electrical systems and safety compliance. Full scope reports with professional assessment and clear recommendations.",
      image: "/assets/img/electrical.jpg",
      includes: [
        "Full scope electrical inspection",
        "Qualified inspection",
        "Insured reporting",
        "Safety compliance check",
        "Digital report with photos",
        "Professional assessment",
        "Clear recommendations",
        "Fast turnaround"
      ],
      pricing: "From $585 incl. GST",
      note: "Full scope professional electrical assessment",
      faqs: [
        {
          question: "Do you perform electrical inspections?",
          answer: "We work with trusted partners to deliver quality insured electrical reports. Our partners are fully qualified and insured to provide comprehensive electrical assessments."
        },
        {
          question: "How do I arrange an electrical inspection?",
          answer: "Contact us and we'll coordinate with our trusted partners to arrange your electrical inspection. We'll ensure smooth scheduling and delivery."
        }
      ]
    }
  };

  const currentService = servicesData[serviceId] || servicesData["pre-sale"];
  const ServiceIcon = currentService.icon;

  // "From $455 incl. GST" -> 455. schema.org requires a NUMBER for a price;
  // the prose string these pages used to emit was discarded by every parser,
  // so the one machine-readable price per service was being thrown away.
  const minPrice = Number(String(currentService.pricing).replace(/[^0-9.]/g, ""));
  const onRequest = !!currentService.priceOnRequest;

  // Service schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": currentService.title,
    "description": currentService.description,
    "provider": businessRef,
    "areaServed": {
      "@type": "City",
      "name": "Darwin"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "AUD",
      // A price-on-request service publishes no number: inventing one would be
      // the false machine-readable price this block exists to avoid.
      ...(onRequest ? {} : {
        "priceSpecification": {
          "@type": "PriceSpecification",
          "minPrice": minPrice,
          "priceCurrency": "AUD",
          "valueAddedTaxIncluded": true
        }
      }),
      "availableAtOrFrom": {
        "@type": "Place",
        "address": POSTAL_ADDRESS
      },
      "seller": businessRef
    }
  };

  return (
    <div>
      <SEO 
        title={currentService.seoTitle || currentService.title}
        description={currentService.description}
        keywords={`${currentService.title} Darwin, ${currentService.title.toLowerCase()}, property inspection Darwin`}
        url={`/services/${serviceId}`}
        schema={serviceSchema}
        // seoTitle already carries "Darwin", so the default
        // "| Aussie Inspections Darwin" suffix would say it twice.
        titleSuffix="Aussie Inspections"
      />

      {/* The banner default quotes $260 — the POOL price. On a $585 electrical
          page that is the first number a reader or a model sees, ~600 words
          before the real one. Each service states its own price. */}
      <CTABanner
        subline={onRequest ? "Contact us for pricing • Quoted up front, no surprises" : `${currentService.pricing} • 72-hour report delivery • Flat-rate, no surprises`}
      />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center bg-slate-900">
        <div className="absolute inset-0">
          <img
            src={currentService.image}
            alt={currentService.title}
            className="w-full h-full object-cover opacity-40"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <ServiceIcon className="w-16 h-16 text-secondary mb-6" />
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none text-white mb-4">
              {currentService.title}
            </h1>
            <p className="text-xl text-slate-200">{currentService.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-6">
                  What's Included
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  {currentService.description}
                </p>

                <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 mb-12">
                  <h3 className="font-chivo text-xl font-bold text-slate-900 mb-6">
                    Our Inspection Includes:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {currentService.includes.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                  <div className="flex items-start space-x-4">
                    <Clock className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{onRequest ? "Fast Turnaround" : "72 Hour Delivery"}</h4>
                      <p className="text-slate-600 text-sm">{onRequest ? "Timing confirmed with your quote" : "Fast turnaround to keep your property timeline moving"}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <DollarSign className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{onRequest ? "Quoted Up Front" : "Flat-Rate Pricing"}</h4>
                      <p className="text-slate-600 text-sm">{onRequest ? "No surprises \u2014 you agree the price before we start" : "No surprises \u2014 transparent pricing from the start"}</p>
                    </div>
                  </div>
                </div>

                {/* FAQs */}
                <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {currentService.faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left font-semibold text-slate-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24"
              >
                {/* Pricing Card */}
                <div className="bg-primary text-white p-8 rounded-lg mb-6">
                  <h3 className="font-chivo text-2xl font-bold mb-2">
                    {currentService.pricing}
                  </h3>
                  <p className="text-blue-100 mb-2">{currentService.note}</p>
                  <p className="text-sm text-blue-100 mb-6">{onRequest ? "Tell us about the property and we will quote" : "Standard pricing for Darwin and Greater NT"}</p>
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase"
                    data-testid="service-book-button"
                  >
                    <Link to="/contact">{onRequest ? "Get a Quote" : "Book Now"}</Link>
                  </Button>
                </div>

                {/* Other Services */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <h3 className="font-chivo text-lg font-bold text-slate-900 mb-4">
                    Our Other Reports
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(servicesData)
                      .filter(([key]) => key !== serviceId)
                      .map(([key, service]) => (
                        <Link
                          key={key}
                          to={`/services/${key}`}
                          className="flex items-center space-x-3 text-slate-700 hover:text-primary transition-colors group"
                          data-testid={`sidebar-service-${key}`}
                        >
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          <span>{service.title}</span>
                        </Link>
                      ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-4xl font-bold text-slate-900 mb-4">
            Ready to Book Your Inspection?
          </h2>
          <p className="text-lg text-slate-600 mb-2">
            Fast, hassle-free booking for agents and homeowners
          </p>
          <p className="text-slate-600 mb-8">
            {onRequest ? `${currentService.pricing} • Quoted up front` : `72-hour report delivery • ${currentService.pricing} • Flat-rate pricing`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
              data-testid="service-cta-button"
            >
              <Link to="/contact">Book Online</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-primary/20 hover:border-primary hover:bg-primary/5 font-bold tracking-wide uppercase px-12"
            >
              <a href={`tel:${BUSINESS_PHONE}`}>Call Now</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
